import { Clock, CheckCircle, Flame } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface DailyProgressProps {
  activeSeconds: number
  progressPct: number
  completedMinimum: boolean
  remainingSeconds: number
  currentStreak: number
  multiplier: number
  notifications: string[]
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function DailyProgress({
  activeSeconds,
  progressPct,
  completedMinimum,
  remainingSeconds,
  currentStreak,
  multiplier,
  notifications,
}: DailyProgressProps) {
  const targetSeconds = 15 * 60 // 15 minutes

  return (
    <div className="card relative overflow-hidden">
      {/* Notifications */}
      <AnimatePresence>
        {notifications.map((notification, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-0 left-0 right-0 bg-quest-accent text-black px-4 py-2 text-center font-medium z-10"
          >
            {notification}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {completedMinimum ? (
            <CheckCircle className="w-6 h-6 text-green-500" />
          ) : (
            <Clock className="w-6 h-6 text-quest-accent" />
          )}
          <h3 className="text-lg font-bold">
            {completedMinimum ? "Daily Goal Complete!" : "Today's Goal"}
          </h3>
        </div>

        {/* Streak indicator */}
        {currentStreak > 0 && (
          <div className="flex items-center gap-1 text-orange-500">
            <Flame className="w-5 h-5" />
            <span className="font-bold">{currentStreak}</span>
            <span className="text-sm text-gray-400">day streak</span>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-400">
            {formatTime(activeSeconds)} / {formatTime(targetSeconds)}
          </span>
          <span className={`font-medium ${completedMinimum ? 'text-green-500' : 'text-white'}`}>
            {Math.round(progressPct)}%
          </span>
        </div>
        <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
          <motion.div
            className={`h-full ${completedMinimum ? 'bg-green-500' : 'bg-quest-primary'}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, progressPct)}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Status message */}
      <div className="text-sm text-gray-400">
        {completedMinimum ? (
          <span className="text-green-400">
            Keep going for bonus XP! Every extra 5 minutes counts.
          </span>
        ) : (
          <span>
            {remainingSeconds > 0 ? (
              <>Only <span className="text-white font-medium">{formatTime(remainingSeconds)}</span> more to complete your daily goal</>
            ) : (
              "Almost there!"
            )}
          </span>
        )}
      </div>

      {/* Multiplier badge */}
      {multiplier > 1 && (
        <div className="mt-3 flex items-center gap-2">
          <span className="px-2 py-1 bg-quest-accent/20 text-quest-accent rounded-full text-xs font-medium">
            {multiplier}x XP MULTIPLIER
          </span>
          <span className="text-xs text-gray-500">
            from your {currentStreak}-day streak
          </span>
        </div>
      )}

      {/* Completion celebration */}
      {completedMinimum && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-4 p-3 bg-green-900/30 border border-green-700/50 rounded-lg"
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="font-medium text-green-400">Daily minimum complete!</span>
          </div>
          <p className="text-sm text-gray-400 mt-1">
            You can keep earning XP, or come back tomorrow to continue your streak.
          </p>
        </motion.div>
      )}
    </div>
  )
}
